import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { fetchClient, updateClient } from "api/clients"
import { useMutation, useQuery } from "react-query"

import { Address } from "./Address"
import { Box } from "@material-ui/core"
import { Client } from "models/common"
import { Loading } from "components/Loading"
import { TextInput } from "components/inputs/TextInput"
import { agencyClientStyles } from "../../AgencyClients/Details/styles"
import { fetchMarket } from "api/regions"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { FieldWithLoader } from "ui/inputs/FieldWithLoader"

export function MarketClientProfile() {
  const { id } = useParams<{ id: string }>()
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const sendFeedback = useFeedback((s) => s.send)

  const [state, setState] = useState<Partial<Client>>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  const { data, isLoading, updatedAt } = useQuery(
    ["market-client", id],
    fetchClient,
    {
      enabled: !!id,
      onError: (e) => sendFeedback(t(e.message), "error"),
    },
  )

  const { data: market, status: marketStatus } = useQuery(
    ["markets", data?.marketId],
    fetchMarket,
    {
      enabled: data?.marketId,
    },
  )

  const [patch] = useMutation(updateClient, {
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt?.target
    setState((s) => ({ ...s, [name]: value }))
  }

  const handleBlur = useCallback(
    (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name = "", value = "" } = evt?.target ?? {}
      const hasDiff =
        data?.[name as keyof Client] !== state?.[name as keyof Client]
      if (hasDiff) {
        patch({ id, [name]: value })
      }
    },
    [patch, id, data, state],
  )

  const isIntercompany = !!data?.intercompany?.id

  useEffect(() => {
    setState(() => data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <Box display="grid" gridAutoFlow="row" padding="var(--spacing-xxl-2) 0 0">
        <h5 className="BodyBody-1BlackBold">
          {t("vendors.profile.section.details")}
        </h5>
        <Box
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="max-content"
          gridColumnGap="1rem"
          alignItems="center"
          padding="0"
          marginTop="var(--spacing-xs)"
        >
          <TextInput
            name="localName"
            value={data?.localName || ""}
            label={t("agency.client.localName")}
            margin="normal"
            size="medium"
            disabled={true}
            className={agencyClientStyles.fieldWidth}
          />
          <TextInput
            name="groupName"
            value={data?.groupName || ""}
            label={t("agency.client.client-groups")}
            margin="normal"
            size="medium"
            className={agencyClientStyles.fieldWidth}
            disabled={true}
          />
        </Box>
        <Box
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="max-content"
          gridColumnGap="1rem"
          alignItems="center"
          padding="0"
        >
          <FieldWithLoader status={marketStatus}>
            <TextInput
              name="marketName"
              value={market?.name || ""}
              label={t("agency.client.market")}
              margin="normal"
              size="medium"
              className={agencyClientStyles.fieldWidth}
              disabled={true}
            />
          </FieldWithLoader>
          <TextInput
            name="taxRegistrationNumber"
            value={state?.taxRegistrationNumber || ""}
            label={t("agency.client.taxRegistrationNumber")}
            onChange={handleChange}
            onBlur={handleBlur}
            margin="normal"
            size="medium"
            className={agencyClientStyles.fieldWidth}
            disabled={!hasAuthority(["AUTH_EDIT_CLIENT_PROFILE"])}
            error={!!errors && !!errors["taxRegistrationNumber"]}
            errorMessage={!!errors ? errors["taxRegistrationNumber"] : ""}
          />
          {isIntercompany ? (
            <TextInput
              name="companyIpg"
              value={data?.intercompany?.name || ""}
              label={t("agency.client.ipg.name")}
              margin="normal"
              size="medium"
              className={agencyClientStyles.fieldWidth}
              disabled={true}
            />
          ) : null}
        </Box>
        <Box
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="max-content"
          gridColumnGap="1rem"
          alignItems="center"
          padding="0"
        >
          <TextInput
            name="profit"
            value={data?.profit || ""}
            label={t("agency.client.profit")}
            margin="normal"
            size="medium"
            className={agencyClientStyles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="cost"
            value={data?.cost || ""}
            label={t("agency.client.cost")}
            margin="normal"
            size="medium"
            className={agencyClientStyles.fieldWidth}
            disabled={true}
          />
        </Box>
      </Box>
      {!!data?.companies?.length ? (
        <Box display="grid" gridAutoFlow="row" padding="var(--spacing-xxl-2) 0">
          <h5 className="BodyBody-1BlackBold">
            {t("vendors.profile.section.companies")}
          </h5>
          {data?.companies?.map((c) => (
            <Fragment key={`companies.${c.id}`}>
              <Box
                display="grid"
                gridAutoFlow="column"
                gridAutoColumns="max-content"
                gridColumnGap="1rem"
                alignItems="center"
                padding="0"
                marginTop="var(--spacing-xs)"
              >
                <TextInput
                  name="company"
                  value={c?.name || ""}
                  label={t(`agency.client.company.name`)}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  disabled={true}
                />
                <TextInput
                  name="code"
                  value={c?.clientCode || ""}
                  label={t(`agency.client.company.code`)}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  disabled={true}
                />
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : null}
      <Box>
        <h5 className="BodyBody-1BlackBold">{t("agency.client.addresses")}</h5>
        <Box
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="max-content"
          gridColumnGap="140px"
          padding="var(--spacing-xl) 0"
          marginBottom="var(--spacing-xxl-3)"
        >
          <Address address="businessAddress" source={data?.businessAddress} />
        </Box>
      </Box>
    </>
  )
}
